/**
 * Agent Progress 模块类型定义
 *
 * 复用原 contact-progress 模块的 swagger 类型，仅在 feature 内补充：
 *  - 容器组件 props
 *  - hook 返回值
 *  - 内部受控弹窗 props
 *
 * 与原 contact-progress 类型保持兼容，不重新定义同名 swagger 模型。
 */

import type {
  IApiInquiryStats,
  IApiCompanyDto,
  IApiTaskReplyDto,
  IApiInquiryRequestResp,
  IApiInquiryDto,
  IApiInquiryDetailResp,
  IApiInquiryContactResp,
} from '@/services/swagger/easourcing/models';

// ---------------------------------------------------------------------------
// Re-exports（让 feature 内部统一从这里取，方便后续替换/扩展）
// ---------------------------------------------------------------------------

export type {
  IApiInquiryStats,
  IApiCompanyDto,
  IApiTaskReplyDto,
  IApiInquiryRequestResp,
  IApiInquiryDto,
  IApiInquiryDetailResp,
  IApiInquiryContactResp,
};

// ---------------------------------------------------------------------------
// 视图层补充字段
// ---------------------------------------------------------------------------

/** stats 在响应中实际带 createTimestamp（swagger 未导出，但接口返回） */
export type AgentInquiryStats = IApiInquiryStats & { createTimestamp?: number };

/** 完整详情视图模型（覆盖 stats 字段为补充类型） */
export interface AgentInquiryDetail extends Omit<IApiInquiryDetailResp, 'stats'> {
  stats: AgentInquiryStats;
}

// ---------------------------------------------------------------------------
// 列表筛选 / 分页参数
// ---------------------------------------------------------------------------

/** 公司列表 tab 类型，与原 contact-progress 完全一致 */
export type AgentContactFilterType = 'all' | 'replied' | 'log';

/** 分页常量 */
export const AGENT_PROGRESS_PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Hook 返回值
// ---------------------------------------------------------------------------

export interface UseAgentInquiryResult {
  /** 当前 inquiry 全量详情 */
  detail: AgentInquiryDetail | null;
  /** stats 快捷访问（detail.stats 别名） */
  stats: AgentInquiryStats | null;
  /** Request Details 弹窗专用数据（懒加载） */
  requestDetail: IApiInquiryRequestResp | null;
  /** Request Details loading（弹窗 loading 用） */
  requestDetailLoading: boolean;
  /** 整体 loading（首屏） */
  loading: boolean;
  /** Pause/Resume/Rename loading 标记（按按钮使用） */
  operating: boolean;
  /** 重新拉取 detail */
  refresh: () => Promise<void>;
  /** 拉取 Request Detail（首次或刷新） */
  fetchRequestDetail: () => Promise<void>;
  /** Pause/Resume：返回 needConfirm 时表示 runningInquiries >= 3 需要弹替换弹窗 */
  toggleRunning: () => Promise<{ needConfirm: boolean; runningInquiries: IApiInquiryDto[] }>;
  /** 替换运行中的某个任务，恢复当前任务 */
  replaceRunningInquiry: (runningInquiryId: string) => Promise<void>;
  /** 重命名（operate=3） */
  renameInquiry: (newName: string) => Promise<void>;
}

export interface UseInquiryContactsResult {
  /** inquiry-contact/list 接口返回的联系人列表 */
  contacts: IApiInquiryContactResp[];
  /** @deprecated 兼容旧引用，与 contacts 相同数据映射为 IApiCompanyDto[] */
  companies: IApiCompanyDto[];
  total: number;
  page: number;
  pageSize: number;
  filter: AgentContactFilterType;
  loading: boolean;
  /** 按 companyTaskId 缓存的 reply 数据 */
  repliesMap: Record<string, IApiTaskReplyDto[]>;
  loadingReplies: Set<string>;
  expandedCompanyTaskId: string | null;
  setPage: (page: number) => void;
  setFilter: (filter: AgentContactFilterType) => void;
  refresh: () => Promise<void>;
  fetchReplies: (companyTaskId: string, onFetched?: (replies: IApiTaskReplyDto[]) => void) => Promise<void>;
  toggleExpand: (companyTaskId: string) => void;
  setExpandedCompanyTaskId: (id: string | null) => void;
}

// ---------------------------------------------------------------------------
// 组件 Props
// ---------------------------------------------------------------------------

export interface AgentProgressPageProps {
  inquiryId: string;
  sessionId?: string;
  onClose?: () => void;
  onNameChange?: (name: string) => void;
  onStateChange?: () => void;
  className?: string;
}

export interface AgentProgressHeaderProps {
  stats: AgentInquiryStats;
  formattedCreateTime: string;
  operating: boolean;
  onPause: () => void;
  onResume: () => void;
  onOpenRequestDetail: () => void;
  onOpenNameEdit: () => void;
  onClose?: () => void;
}

export interface InquirySummaryCardProps {
  stats: AgentInquiryStats;
  isComputation: boolean;
  computationText: string;
  contactProgress: number;
}

export interface AgentRunningLogSectionProps {
  inquiryId: string;
  /** detail.stats?.status 以决定是否轮询 */
  status?: number;
}

export interface EditInquiryNameDialogProps {
  open: boolean;
  defaultName: string;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  /** Save 触发：抛错时由调用方决定是否保留弹窗 */
  onSubmit: (name: string) => Promise<void> | void;
}

// ---------------------------------------------------------------------------
// Figma Rebuild VM 层
//
// 视图模型（VM）类型定义，对应 specs/001-agent-progress-figma-rebuild/data-model.md。
// 由 `lib/view-model.ts` 中的纯函数从既有 swagger DTO 派生而来；组件只接收 VM。
// 缺字段一律以 '—' 占位，禁止把 undefined 透传给组件。
// ---------------------------------------------------------------------------

/** Inquiry 顶层状态（由 stats.status 1/2/3 映射而来） */
export type InquiryStatus = 'running' | 'completed' | 'paused';

/** Header 区域 VM */
export interface AgentProgressHeaderVM {
  /** 标题：inquiry.taskName / inquiry.productName / 'Untitled Inquiry' */
  title: string;
  status: InquiryStatus;
  /** 'In progress' | 'Completed' | 'Paused' */
  statusLabel: string;
  /** 0~100 */
  progressPct: number;
  /** 'Date Created: Oct 24, 2024' */
  dateCreatedDisplay: string;
  /** false 时不渲染 Pencil（status === 'completed'） */
  canRename: boolean;
  /** false 时 Pause/Resume 按钮 disabled */
  canPauseResume: boolean;
}

/** Inquiry Details 字段单元格 */
export interface InquiryDetailsField {
  label: string;
  value: string;
}

/** Inquiry Details VM —— 6 个字段固定顺序 */
export interface InquiryDetailsVM {
  fields: [
    InquiryDetailsField, // Product Name
    InquiryDetailsField, // Unit Price
    InquiryDetailsField, // Quantity
    InquiryDetailsField, // Trade terms
    InquiryDetailsField, // Buyer Name
    InquiryDetailsField, // Location
  ];
}

/** Reply 弹窗内单个联系人 VM */
export interface ContactInReplyVM {
  id: string;
  companyName: string;
  contactName: string;
  /** 该供应商提出的原始问题 */
  originalQuestion: string;
  /** 跳转链接（在新标签页打开） */
  viewDetailsHref: string;
}

/** Clarification Board 中单个问题 VM */
export interface ClarificationItemVM {
  /** clarification id（用于 reply 提交） */
  id: string;
  question: string;
  /** 关联供应商数（≥ 1 才入列表） */
  supplierCount: number;
  /** Reply 弹窗的联系人列表 */
  contacts: ContactInReplyVM[];
}

/** Clarification Board VM；items 为空时父层不渲染整卡片（构造器返回 null） */
export interface ClarificationBoardVM {
  repliedCount: number;
  totalCount: number;
  /** 'Replied 3/5' */
  repliedBadgeText: string;
  items: ClarificationItemVM[];
}

/** Suppliers Table 行状态 */
export type SupplierStatus = 'quote-ready' | 'clarify-needed' | 'awaiting-reply' | 'ai-thinking' | 'completed';

/** Suppliers Table 行 VM */
export interface SupplierRowVM {
  /** contact / companyTask 唯一 id */
  id: string;
  /** 企业 ID（用于打开公司详情侧边栏） */
  companyId: string;
  /** 联系人 ID（用于跳转供应商详情页） */
  contactId: string;
  /** 触发消息 ID（用于定位往来邮件） */
  triggerMsgId: string;
  companyName: string;
  contactName: string;
  email: string;
  status: SupplierStatus;
  /** 'Quote Ready' | 'Clarify Needed' | 'Awaiting Reply' | 'AI thinking' */
  statusLabel: string;
  /** Details 列显示文本（如 clarification question），为空时不渲染 */
  detailsText: string;
  unitPrice: string;
  totalQuotation: string;
  delivery: string;
  payment: string;
  additionalInfo: string;
  aiReplyEnabled: boolean;
}

/** Suppliers Table VM */
export interface SuppliersTableVM {
  rows: SupplierRowVM[];
  /** 1-based */
  page: number;
  pageSize: number;
  total: number;
}

/** Reply 提交载荷（已存在的契约，不新增网络字段） */
export interface SubmitClarificationReplyPayload {
  clarificationId: string;
  /** 用户在 textarea 输入的文本 */
  userReply: string;
}

/** 顶层聚合 VM */
export interface AgentProgressViewModel {
  header: AgentProgressHeaderVM;
  inquiryDetails: InquiryDetailsVM;
  /** null 表示整卡片不渲染（无 clarification 项时） */
  clarificationBoard: ClarificationBoardVM | null;
  suppliersTable: SuppliersTableVM;
}
